<script setup lang="ts">
import { computed } from 'vue'
import {
  LogOut,
  Menu,
  RefreshCw,
  Search,
  User
} from '@lucide/vue'

const props = withDefaults(
  defineProps<{
    brandTitle: string
    brandSubtitle?: string
    brandMark?: string
    breadcrumb: string
    title: string
    username?: string
    loading?: boolean
    showRefresh?: boolean
    showLogout?: boolean
    sidebarCollapsed?: boolean
    mobileMenuOpen?: boolean
  }>(),
  {
    brandSubtitle: '',
    brandMark: 'A',
    username: '',
    loading: false,
    showRefresh: true,
    showLogout: true,
    sidebarCollapsed: false,
    mobileMenuOpen: false
  }
)

const emit = defineEmits<{
  'toggle-sidebar': []
  'toggle-mobile-menu': []
  'open-command-palette': []
  refresh: []
  logout: []
}>()

const breadcrumbSegments = computed(() => {
  if (!props.breadcrumb) return []
  return props.breadcrumb.split('/').map((s) => s.trim()).filter(Boolean)
})
</script>

<template>
  <header class="admin-shell__topbar" :class="{ 'is-collapsed': sidebarCollapsed }">
    <div class="admin-shell__brand-row">
      <div class="admin-shell__brand-mark">{{ brandMark }}</div>
      <div class="admin-shell__brand">
        <strong>{{ brandTitle }}</strong>
        <span v-if="brandSubtitle">{{ brandSubtitle }}</span>
      </div>
    </div>

    <div class="admin-shell__topbar-main">
      <button
        type="button"
        class="admin-shell__icon-button admin-shell__desktop-toggle"
        :aria-label="sidebarCollapsed ? '展开菜单' : '收起菜单'"
        @click="emit('toggle-sidebar')"
      >
        <Menu :size="18" :stroke-width="1.8" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="admin-shell__icon-button admin-shell__mobile-toggle"
        :aria-label="mobileMenuOpen ? '关闭菜单' : '打开菜单'"
        @click="emit('toggle-mobile-menu')"
      >
        <Menu :size="19" :stroke-width="1.8" aria-hidden="true" />
      </button>
      <div class="admin-shell__heading">
        <nav v-if="breadcrumbSegments.length > 0" class="admin-shell__breadcrumb" aria-label="面包屑导航">
          <template v-for="(seg, idx) in breadcrumbSegments" :key="idx">
            <span
              class="admin-shell__breadcrumb-item"
              :class="{ 'is-current': idx === breadcrumbSegments.length - 1 }"
            >
              {{ seg }}
            </span>
            <span v-if="idx < breadcrumbSegments.length - 1" class="admin-shell__breadcrumb-separator" aria-hidden="true">/</span>
          </template>
        </nav>
        <h1 v-else-if="title" class="admin-shell__title">{{ title }}</h1>
      </div>
      <button
        type="button"
        class="admin-shell__command-trigger"
        title="快速导航 (⌘K)"
        @click="emit('open-command-palette')"
      >
        <Search :size="14" :stroke-width="1.8" aria-hidden="true" />
        <span>快速导航</span>
        <kbd>⌘ K</kbd>
      </button>
    </div>

    <div class="admin-shell__actions">
      <slot name="header-actions" />
      <span v-if="username" class="admin-shell__user-badge" :title="`当前登录：${username}`">
        <User :size="13" class="admin-shell__user-icon" aria-hidden="true" />
        <span>{{ username }}</span>
      </span>
      <button
        v-if="showRefresh"
        type="button"
        class="admin-shell__icon-button"
        :disabled="loading"
        title="刷新当前工作区"
        aria-label="刷新当前工作区"
        @click="emit('refresh')"
      >
        <RefreshCw :size="16" :stroke-width="1.8" :class="{ 'is-spinning': loading }" aria-hidden="true" />
      </button>
      <button
        v-if="showLogout"
        type="button"
        class="admin-shell__text-button"
        title="退出登录"
        aria-label="退出登录"
        @click="emit('logout')"
      >
        <LogOut :size="15" :stroke-width="1.8" aria-hidden="true" />
        <span>退出</span>
      </button>
    </div>
  </header>
</template>

<style scoped>
.admin-shell__topbar {
  position: fixed;
  z-index: 200;
  top: 0;
  right: 0;
  left: 0;
  display: grid;
  grid-template-columns: var(--admin-shell-sidebar-width, 230px) minmax(0, 1fr) auto;
  height: var(--admin-shell-topbar-height, 50px);
  background: var(--rr-color-surface);
  border-bottom: 1px solid var(--rr-color-border);
  box-sizing: border-box;
  transition: grid-template-columns 0.24s ease;
}

.admin-shell__brand-row {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-width: 0;
  height: var(--admin-shell-topbar-height, 50px);
  box-sizing: border-box;
  background: var(--rr-color-text-strong);
  color: var(--rr-color-surface-muted);
  overflow: hidden;
  padding: 0 16px;
  transition: padding 0.24s ease;
}

.admin-shell__topbar.is-collapsed .admin-shell__brand-row {
  grid-template-columns: 1fr;
  justify-items: center;
  padding: 0;
}

.admin-shell__topbar.is-collapsed .admin-shell__brand {
  display: none !important;
}

.admin-shell__brand-mark {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-primary);
  color: var(--rr-color-surface);
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 900;
  line-height: 1;
}

.admin-shell__brand {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.admin-shell__brand strong,
.admin-shell__brand span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__brand strong {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.1;
  color: #ffffff;
}

.admin-shell__brand span {
  color: rgb(255 255 255 / 58%);
  font-size: 11px;
}

.admin-shell__topbar-main {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
}

.admin-shell__heading {
  display: flex;
  align-items: center;
  min-width: 0;
}

.admin-shell__breadcrumb {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-shell__breadcrumb-item {
  color: var(--rr-color-text-muted);
  transition: color 120ms ease;
}

.admin-shell__breadcrumb-item.is-current {
  color: var(--rr-color-text-strong);
  font-weight: 600;
}

.admin-shell__breadcrumb-separator {
  color: var(--rr-color-text-subtle);
  margin: 0 6px;
  font-size: 12px;
  user-select: none;
}

.admin-shell__title {
  color: var(--rr-color-text-strong);
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__command-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 12px;
  cursor: pointer;
  margin-left: 8px;
  transition: all 120ms ease;
}

.admin-shell__command-trigger:hover {
  border-color: var(--rr-color-primary-border);
  color: var(--rr-color-text-strong);
  background: var(--rr-color-surface);
}

.admin-shell__command-trigger kbd {
  font-family: inherit;
  font-size: 10px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: 3px;
  padding: 1px 4px;
}

.admin-shell__actions {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 16px;
}

.admin-shell__actions :deep(.notification-bell__trigger),
.admin-shell__actions :deep(.admin-shell__icon-button) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
}

.admin-shell__icon-button,
.admin-shell__text-button {
  border: 0 !important;
  background: transparent !important;
  color: var(--rr-color-text) !important;
  cursor: pointer;
  transition: all 120ms ease;
}

.admin-shell__icon-button {
  display: grid;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  place-items: center;
  border-radius: var(--rr-radius-control);
}

.admin-shell__icon-button:hover,
.admin-shell__text-button:hover {
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-text-strong) !important;
}

.admin-shell__icon-button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.admin-shell__mobile-toggle {
  display: none;
}

.admin-shell__user-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 180px;
  height: 28px;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text);
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  padding: 0 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__user-icon {
  color: var(--rr-color-text-subtle);
  flex-shrink: 0;
}

.admin-shell__text-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  border-radius: var(--rr-radius-control);
  font-size: 12px;
  font-weight: 500;
  padding: 0 8px !important;
  margin: 0 !important;
}

.is-spinning {
  animation: rr-spin 800ms linear infinite;
}

@keyframes rr-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .admin-shell__topbar {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .admin-shell__brand-row {
    display: none;
  }
  .admin-shell__desktop-toggle {
    display: none;
  }
  .admin-shell__mobile-toggle {
    display: grid;
  }
  .admin-shell__command-trigger {
    display: none;
  }
}
</style>
